'use client';
/**
 * The app's `Link` and `useRouter`: next-intl's, with one addition. Every navigation they start is
 * counted in `@/lib/navigation-status` for as long as it is pending, so `<NavigationProgress>` can
 * show that something is happening (docs/10-ux-audit.md · UX-45).
 *
 * Here rather than at each call site on purpose: a link that forgets is a tap that looks ignored,
 * and the app has dozens of them. Nothing else about either export changes — `Link` still renders
 * Next's link (so `useLinkStatus` works inside it), and `useRouter` still takes `{ locale }`.
 */
import * as React from 'react';
import { useLinkStatus } from 'next/link';
import { usePathname as useNextPathname } from 'next/navigation';
import { createNavigation } from 'next-intl/navigation';
import { beginNavigation } from '@/lib/navigation-status';
import { routing } from './routing';

const navigation = createNavigation(routing);

export const { usePathname, getPathname } = navigation;

/** Mirrors the enclosing link's pending state into the app-wide count. Renders nothing. */
function LinkStatusBridge() {
  const { pending } = useLinkStatus();
  React.useEffect(() => (pending ? beginNavigation() : undefined), [pending]);
  return null;
}

export type LinkProps = React.ComponentProps<typeof navigation.Link>;

// `createElement` rather than JSX so this stays `navigation.ts`: the file is imported everywhere, and
// a running `next dev --webpack` caches the resolved path — renaming it takes the whole app down
// until the server restarts.
export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(function Link({ children, ...props }, ref) {
  return React.createElement(navigation.Link, { ref, ...props }, children, React.createElement(LinkStatusBridge));
});

type Router = ReturnType<typeof navigation.useRouter>;

/** The longest a programmatic navigation may hold the bar when the address never changes. */
const NAVIGATION_CAP_MS = 60_000;

/**
 * `push` and `replace` count as pending from the call until the address changes, or until the
 * component that called them is gone with the page it was on. React's transition around a
 * navigation is not a reliable signal on its own: on the editor's history-guarded exit it was seen
 * settling before the new route had rendered, roughly two times in three. `refresh()` changes no
 * address, so it is the one call that ends with its transition — and with the cap if that never
 * reports.
 */
export function useRouter(): Router {
  const router = navigation.useRouter();
  const pathname = useNextPathname();
  const [pending, startTransition] = React.useTransition();
  const end = React.useRef<(() => void) | null>(null);
  const refreshing = React.useRef(false);

  const settle = React.useCallback(() => {
    end.current?.();
    end.current = null;
  }, []);
  const begin = React.useCallback(() => {
    settle();
    const finish = beginNavigation();
    const cap = setTimeout(settle, NAVIGATION_CAP_MS);
    end.current = () => {
      clearTimeout(cap);
      finish();
    };
  }, [settle]);

  // The cleanup runs when the address changes and when this component unmounts — both mean landed.
  React.useEffect(() => settle, [pathname, settle]);
  React.useEffect(() => {
    if (!pending && refreshing.current) {
      refreshing.current = false;
      settle();
    }
  }, [pending, settle]);

  return React.useMemo<Router>(
    () => ({
      ...router,
      push: (...args: Parameters<Router['push']>) => {
        begin();
        router.push(...args);
      },
      replace: (...args: Parameters<Router['replace']>) => {
        begin();
        router.replace(...args);
      },
      refresh: () => {
        begin();
        refreshing.current = true;
        startTransition(() => router.refresh());
      },
    }),
    [router, begin, startTransition],
  );
}
